import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Announcements.css';

const Announcements = () => {
  const { courseId } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [announcements, setAnnouncements] = useState([]);
  const [globalAnnouncements, setGlobalAnnouncements] = useState([]);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchAnnouncements();
  }, [courseId]);

  const fetchAnnouncements = async () => {
    try {
      const requests = [api.get('/announcements/global')];

      if (courseId) {
        requests.push(
          api.get(`/announcements/course/${courseId}`),
          api.get(`/courses/${courseId}`)
        );
      } else {
        requests.push(api.get('/announcements/my-announcements'));
      }

      const responses = await Promise.all(requests);
      setGlobalAnnouncements(responses[0].data);

      if (courseId) {
        setAnnouncements(responses[1].data);
        setCourse(responses[2].data);
      } else {
        setAnnouncements(responses[1].data);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (announcementId) => {
    try {
      await api.post(`/announcements/${announcementId}/read`);
      setAnnouncements((prev) =>
        prev.map((a) =>
          a.id === announcementId ? { ...a, is_read: true } : a
        )
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleExpand = (announcement) => {
    setExpandedId(expandedId === announcement.id ? null : announcement.id);
    if (!announcement.is_read) {
      markAsRead(announcement.id);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'info':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        );
      case 'warning':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      case 'success':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        );
      case 'urgent':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        );
    }
  };

  const filteredAnnouncements = announcements.filter((a) => {
    if (filter === 'unread') return !a.is_read;
    return true;
  });

  const unreadCount = announcements.filter((a) => !a.is_read).length;

  if (loading) return <div className="loading">{t('announcements.loading')}</div>;

  return (
    <div className="page announcements-page">
      <div className="container">
        <header className="announcements-header">
          <div className="header-content">
            <h1>
              {courseId ? `${course?.title} - ${t('announcements.announcementsTitle')}` : t('announcements.announcementsTitle')}
            </h1>
            <p>
              {courseId
                ? t('announcements.stayUpdated')
                : t('announcements.viewAllCourses')}
            </p>
          </div>
          <div className="header-stats">
            <div className="stat-card">
              <span className="stat-value">{announcements.length}</span>
              <span className="stat-label">{t('announcements.total')}</span>
            </div>
            <div className="stat-card unread">
              <span className="stat-value">{unreadCount}</span>
              <span className="stat-label">{t('announcements.unread')}</span>
            </div>
          </div>
        </header>

        <div className="announcements-controls">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              {t('announcements.all')}
            </button>
            <button
              className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              {t('announcements.unread')} ({unreadCount})
            </button>
          </div>
        </div>

        {globalAnnouncements.length > 0 && (
          <section className="global-announcements">
            <h2>{t('announcements.platformUpdates')}</h2>
            <div className="announcements-list global">
              {globalAnnouncements.map((announcement) => (
                <article
                  key={`global-${announcement.id}`}
                  className={`announcement-card global ${announcement.announcement_type} ${announcement.is_pinned ? 'pinned' : ''}`}
                >
                  <div className="announcement-icon">
                    {getTypeIcon(announcement.announcement_type)}
                  </div>
                  <div className="announcement-content">
                    <div className="announcement-header">
                      {announcement.is_pinned && <span className="pin-badge">{t('announcements.pinned')}</span>}
                      <span className={`type-badge ${announcement.announcement_type}`}>
                        {announcement.announcement_type}
                      </span>
                      <h3>{announcement.title}</h3>
                    </div>
                    <p className="announcement-text">{announcement.content}</p>
                    <div className="announcement-meta">
                      <span className="author">
                        By {announcement.first_name} {announcement.last_name}
                      </span>
                      <span className="date">
                        {new Date(announcement.published_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <section className="course-announcements">
          <h2>{courseId ? t('announcements.courseAnnouncementsSection') : t('announcements.fromYourCourses')}</h2>
          {filteredAnnouncements.length === 0 ? (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <p>{t('announcements.noFound')}</p>
              {filter === 'unread' && (
                <button onClick={() => setFilter('all')} className="btn-secondary">
                  {t('announcements.viewAllBtn')}
                </button>
              )}
            </div>
          ) : (
            <div className="announcements-list">
              {filteredAnnouncements.map((announcement) => (
                <article
                  key={announcement.id}
                  className={`announcement-card ${announcement.announcement_type} ${announcement.is_pinned ? 'pinned' : ''} ${!announcement.is_read ? 'unread' : ''} ${expandedId === announcement.id ? 'expanded' : ''}`}
                  onClick={() => handleExpand(announcement)}
                >
                  <div className="announcement-icon">
                    {getTypeIcon(announcement.announcement_type)}
                  </div>
                  <div className="announcement-content">
                    <div className="announcement-header">
                      {!announcement.is_read && <span className="unread-dot" />}
                      {announcement.is_pinned && <span className="pin-badge">{t('announcements.pinned')}</span>}
                      <span className={`type-badge ${announcement.announcement_type}`}>
                        {announcement.announcement_type}
                      </span>
                      <h3>{announcement.title}</h3>
                    </div>
                    <p className="announcement-text">
                      {expandedId === announcement.id
                        ? announcement.content
                        : announcement.content.length > 150
                        ? `${announcement.content.substring(0, 150)}...`
                        : announcement.content}
                    </p>
                    {announcement.link_url && (
                      <a
                        href={announcement.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="announcement-join-btn"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                          <path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.87v6.26a1 1 0 0 1-1.447.894L15 14M3 8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z" />
                        </svg>
                        {t('announcements.joinMeeting')}
                      </a>
                    )}
                    <div className="announcement-meta">
                      {announcement.course_title && (
                        <Link
                          to={`/courses/${announcement.course_id}`}
                          className="course-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {announcement.course_title}
                        </Link>
                      )}
                      <span className="author">
                        {announcement.first_name} {announcement.last_name}
                      </span>
                      <span className="date">
                        {new Date(announcement.published_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="expand-indicator">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points={expandedId === announcement.id ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                    </svg>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Announcements;
